import { AccountPeriodsNames, classNames } from "utils";
import { Badge, Show } from "components";
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";

import { Fragment } from "react";
import { Payment_status } from "_graphql/__generated__/globalTypes";
import RemoveQuarterContainer from "./remove-quarter";
import { Treaty_treaty } from "_graphql/queries/__generated__/Treaty";
import UpdateQuarterContainer from "./update-quarter";
import ViewTreatyDebitNoteContainer from "./view-treaty-account-debit-note";
import _ from "lodash";

type Props = {
  treaty: Treaty_treaty | null | undefined;
};

const TreatyAccounts = ({ treaty }: Props) => {
  const navigate = useAppNavigate();
  const searchParams = useAppSearch();

  return (
    <div className="bg-card-500 rounded-lg shadow-lg">
      <Show if={Boolean(treaty?.treaty_accounts && treaty?.treaty_accounts?.length > 0)}>
        <div className="px-8 py-6 border-b border-gray-700/50">
          <h2 className="text-xl font-semibold text-primary">Quarterly Accounts</h2>
          <p className="mt-1 text-sm text-tertiary">
            Review and manage treaty accounts for each quarter
          </p>
        </div>

        <div className="p-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-2">
            {treaty?.treaty_accounts?.map((account) => (
              <div
                key={account?.treaty_account_id}
                className="bg-card-600 rounded-xl border border-card-500 shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className="p-6 border-b border-background-400">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-background-500 rounded-lg">
                        <DocumentTextIcon className="h-8 w-8 text-blue-600" />
                      </div>
                      <div className="flex flex-col space-y-3">
                        <h3 className="text-lg font-semibold text-primary">
                          {_.get(
                            AccountPeriodsNames,
                            account?.account_periods || "FIRSTQUARTER",
                            ""
                          )}
                        </h3>
                        <p className="text-sm text-gray-500">
                          <Badge label={account?.payment_status || ""} status={account?.payment_status === Payment_status.PAID ? "success" : account?.payment_status === Payment_status.PARTPAYMENT ? "warning" : "error"} />

                        </p>
                      </div>
                    </div>

                    <Menu as="div" className="relative">
                      <Menu.Button className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200">
                        <EyeIcon className="h-5 w-5 text-gray-500" />
                      </Menu.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <div className="py-1">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  className={classNames(
                                    active ? "bg-gray-50" : "",
                                    "flex w-full items-center px-4 py-2 text-sm text-gray-700"
                                  )}
                                  onClick={() =>
                                    navigate({
                                      search(prev) {
                                        return {
                                          ...prev,
                                          treaty_account_id: account?.treaty_account_id || "",
                                          modal: "calculate",
                                        };
                                      },
                                    })
                                  }
                                >
                                  <DocumentTextIcon className="mr-3 h-5 w-5 text-gray-400" />
                                  View Documents
                                </button>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  className={classNames(
                                    active ? "bg-gray-50" : "",
                                    "flex w-full items-center px-4 py-2 text-sm text-gray-700"
                                  )}
                                  onClick={() =>
                                    navigate({
                                      search(prev) {
                                        return {
                                          ...prev,
                                          treaty_account_id: account?.treaty_account_id || "",
                                          modal: "update",
                                        };
                                      },
                                    })
                                  }
                                >
                                  <PencilSquareIcon className="mr-3 h-5 w-5 text-gray-400" />
                                  Edit Quarter
                                </button>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  className={classNames(
                                    active ? "bg-gray-50" : "",
                                    "flex w-full items-center px-4 py-2 text-sm text-red-600"
                                  )}
                                  onClick={() =>
                                    navigate({
                                      search(prev) {
                                        return {
                                          ...prev,
                                          treaty_account_id: account?.treaty_account_id || "",
                                          modal: "remove",
                                        };
                                      },
                                    })
                                  }
                                >
                                  <TrashIcon className="mr-3 h-5 w-5 text-red-400" />
                                  Remove Quarter
                                </button>
                              )}
                            </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </div>
                </div>

                {/* <div className="p-6 space-y-4">
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-400">Cash Loss</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {account?.treaty_account_deduction?.map((deduction, index) => (
                        <div key={index} className="bg-background-600 rounded-lg p-3">
                          <p className="text-sm font-medium text-gray-400">Surplus {index + 1}</p>
                          <p className="mt-1 text-lg font-semibold text-gray-300">
                            {deduction?.cash_loss}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-400">Claims Settled</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {account?.treaty_account_deduction?.map((deduction, index) => (
                        <div key={index} className="bg-background-600 rounded-lg p-3">
                          <p className="text-sm font-medium text-gray-400">Surplus {index + 1}</p>
                          <p className="mt-1 text-lg font-semibold text-gray-300">
                            {deduction?.claim_settled}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div> */}
              </div>
            ))}
          </div>
        </div>
      </Show>


      <>
        {searchParams?.treaty_account_id && searchParams?.modal === "calculate" && (<ViewTreatyDebitNoteContainer
          open={searchParams?.modal === "calculate"}
          setOpen={(val: boolean) =>
            navigate({
              search(prev) {
                return {
                  ...prev,
                  modal: val ? "calculate" : undefined,
                  treaty_account_id: val ? prev?.treaty_account_id : undefined,
                };
              },
            })
          }
        />)}
        {searchParams?.treaty_account_id && searchParams?.modal === "remove" && <RemoveQuarterContainer
          open={searchParams?.modal === "remove"}
          setOpen={(val: boolean) =>
            navigate({
              search(prev) {
                return {
                  ...prev,
                  modal: val ? "remove" : undefined,
                  treaty_account_id: val ? prev?.treaty_account_id : undefined,
                };
              },
            })
          }
        />}
        {searchParams?.treaty_account_id && searchParams?.modal === "update" && <UpdateQuarterContainer
          open={searchParams?.modal === "update"}
          setOpen={(val: boolean) =>
            navigate({
              search(prev) {
                return {
                  ...prev,
                  modal: val ? "update" : undefined,
                  treaty_account_id: val ? prev?.treaty_account_id : undefined,
                };
              },
            })
          }
        />}
      </>
    </div>
  );
};

export default TreatyAccounts;
